import React from 'react';
import { HashLink } from 'react-router-hash-link';

export default function TrendsSection() {
  return (
    <section id="trends" className="sec">
      <div className="trends-inner">
        <div className="trends-top rv">
          <div>
            <div className="kicker k-gold">Market Intelligence</div>
            <h2 className="title lt">
              Masterbatch Trend
              <br />
              <em>Intelligence 2025–2030</em>
            </h2>
            <div className="trends-badge">Auralux Proprietary Research</div>
          </div>
          <p className="trends-sub">
            The global masterbatch market is undergoing its most significant structural shift in two decades. Regulatory
            pressure, circular economy mandates, and rapid advances in functional additives are redefining what&apos;s
            possible — and what&apos;s required. Auralux tracks these trends continuously so our customers stay ahead.
          </p>
        </div>

        <div className="trends-grid rv">
          <div className="trend-card">
            <div className="trend-year">Trend 01 · 2025–2027</div>
            <h3>Circular Economy &amp; rPET-Compatible Formulations</h3>
            <p>
              Mandates from the EU Packaging and Packaging Waste Regulation (PPWR) require 30% recycled content in PET
              packaging by 2030. Masterbatch producers must reformulate pigments, carriers, and additives to survive
              mechanical recycling without color migration or IV drop.
            </p>
            <span className="trend-tag">HIGH URGENCY</span>
          </div>
          <div className="trend-card">
            <div className="trend-year">Trend 02 · 2025–2028</div>
            <h3>NIR-Sortable &amp; Laser-Marking Masterbatches</h3>
            <p>
              Infrared-based sorting at Materials Recovery Facilities (MRFs) is becoming the global standard.
              Carbon-black alternatives with NIR transparency and laser-markable grades are replacing conventional black
              pigments across packaging, automotive, and electronics applications.
            </p>
            <span className="trend-tag">FAST GROWING</span>
          </div>
          <div className="trend-card">
            <div className="trend-year">Trend 03 · 2025–2030</div>
            <h3>Bio-Based Carrier Systems &amp; Bioplastic Compatibility</h3>
            <p>
              PLA, PBAT, TPS, and PHB-based polymers are scaling globally. Masterbatch carriers designed specifically for
              bioplastics — with compatible thermal profiles and no contamination risk — represent one of the
              fastest-growing niche segments in the industry through 2030.
            </p>
            <span className="trend-tag">HIGH GROWTH</span>
          </div>
          <div className="trend-card">
            <div className="trend-year">Trend 04 · 2026–2029</div>
            <h3>AI-Driven Digital Color Formulation &amp; Matching</h3>
            <p>
              Machine learning models trained on spectrophotometric datasets are compressing color development cycles
              from 4–6 weeks to under 72 hours. Digital twin technologies allow virtual validation across multiple
              polymer substrates before any physical trial is run.
            </p>
            <span className="trend-tag">TRANSFORMATIVE</span>
          </div>
          <div className="trend-card">
            <div className="trend-year">Trend 05 · 2025–2028</div>
            <h3>Multifunctional &amp; Smart Additive Masterbatches</h3>
            <p>
              Brand owners are demanding single masterbatches that deliver color plus UV protection, antimicrobial
              properties, or oxygen scavenging simultaneously. Combination masterbatches reduce SKU complexity, lower LDR,
              and improve supply chain efficiency for converters.
            </p>
            <span className="trend-tag">EMERGING</span>
          </div>
          <div className="trend-card">
            <div className="trend-year">Trend 06 · 2026–2030</div>
            <h3>Regulatory Clean Chemistry: REACH, PFAS &amp; EU Taxonomy</h3>
            <p>
              The PFAS restriction under REACH, EU Taxonomy green finance standards, and expanding food-contact
              legislation are forcing systematic reformulation of pigment portfolios. Heavy-metal-free,
              migration-compliant formulations are shifting from premium to baseline requirement.
            </p>
            <span className="trend-tag">REGULATORY DRIVEN</span>
          </div>
        </div>

        <div className="trend-timeline rv">
          <div className="tt-header">
            <h3>
              Segment Growth Outlook — <em>2025 to 2030</em>
            </h3>
            <span className="trend-tag" style={{ color: 'var(--gold)', borderColor: 'rgba(212,175,55,.3)' }}>
              Global Masterbatch Market
            </span>
          </div>
          <div className="tt-row">
            <div className="tt-head">Segment</div>
            <div className="tt-head">2025</div>
            <div className="tt-head">2026</div>
            <div className="tt-head">2027</div>
            <div className="tt-head">2028</div>
            <div className="tt-head">CAGR</div>
          </div>
          <div className="tt-row">
            <div className="tt-label">Color MB</div>
            <div className="tt-cell">$4.8B</div>
            <div className="tt-cell">$5.2B</div>
            <div className="tt-cell">$5.6B</div>
            <div className="tt-cell">$6.1B</div>
            <div className="tt-cell hi">+6.2%</div>
          </div>
          <div className="tt-row">
            <div className="tt-label">White MB</div>
            <div className="tt-cell">$2.1B</div>
            <div className="tt-cell">$2.3B</div>
            <div className="tt-cell">$2.5B</div>
            <div className="tt-cell">$2.7B</div>
            <div className="tt-cell md">+5.8%</div>
          </div>
          <div className="tt-row">
            <div className="tt-label">Additive MB</div>
            <div className="tt-cell">$3.4B</div>
            <div className="tt-cell">$3.8B</div>
            <div className="tt-cell">$4.3B</div>
            <div className="tt-cell">$4.9B</div>
            <div className="tt-cell hi">+8.9%</div>
          </div>
          <div className="tt-row">
            <div className="tt-label">rPET / Bio MB</div>
            <div className="tt-cell">$0.6B</div>
            <div className="tt-cell">$0.9B</div>
            <div className="tt-cell">$1.4B</div>
            <div className="tt-cell">$2.1B</div>
            <div className="tt-cell hi">+23.4%</div>
          </div>
          <div className="tt-row">
            <div className="tt-label">NIR / Laser</div>
            <div className="tt-cell">$0.3B</div>
            <div className="tt-cell">$0.5B</div>
            <div className="tt-cell">$0.8B</div>
            <div className="tt-cell">$1.2B</div>
            <div className="tt-cell hi">+32.1%</div>
          </div>
        </div>

        <div className="trend-insight rv">
          <div className="ti-text">
            <h4>Access the Full 2025–2030 Intelligence Report</h4>
            <p>
              Detailed analysis covering 18 polymer segments, 42 country markets, regulatory timelines, competitive
              landscape, and Auralux&apos;s strategic positioning for each trend vector. Updated quarterly.
            </p>
          </div>
          <HashLink smooth to="#contact" className="btn-p" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            Request Full Report →
          </HashLink>
        </div>
      </div>
    </section>
  );
}
